'use client';

import { useRef, useEffect } from 'react';
import type { ImageField, RichTextField } from '@prismicio/client';
import { asHTML } from '@prismicio/helpers';
import { PrismicRichText } from '@prismicio/react';
import Image from 'next/image';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/dist/ScrollTrigger';

// Colors
const UNFILLED = '#C8D0D2'; // muted grey — starting state
const PRIMARY  = '#111827'; // gray-900 — all words fill to this

interface WordData {
  text: string;
  isBold: boolean;
}

/** Parse Prismic HTML, splitting into words and flagging <strong> words as bold. */
function parseWords(html: string): WordData[] {
  const result: WordData[] = [];
  const decodeEntities = (s: string) =>
    s.replace(/&amp;/g, '&')
     .replace(/&lt;/g, '<')
     .replace(/&gt;/g, '>')
     .replace(/&quot;/g, '"')
     .replace(/&#39;/g, "'")
     .replace(/&nbsp;/g, '\u00A0');
  const stripTags = (s: string) => decodeEntities(s.replace(/<[^>]*>/g, ''));
  const push = (text: string, isBold: boolean) =>
    text.split(/\s+/).filter(Boolean).forEach(w => result.push({ text: w, isBold }));

  const strongRegex = /<strong>(.*?)<\/strong>/g;
  let lastIndex = 0;
  let match;

  while ((match = strongRegex.exec(html)) !== null) {
    if (match.index > lastIndex) push(stripTags(html.slice(lastIndex, match.index)), false);
    push(stripTags(match[1]), true);
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < html.length) push(stripTags(html.slice(lastIndex)), false);

  return result;
}

interface IntegrationIntroProps {
  primary?: RichTextField;
  secondary?: RichTextField;
  image?: ImageField;
}

export default function IntegrationIntro({ primary, secondary, image }: IntegrationIntroProps) {
  const sectionRef = useRef<HTMLElement>(null);
  const imageRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    gsap.registerPlugin(ScrollTrigger);

    const section = sectionRef.current;
    if (!section) return;

    const allWords = Array.from(section.querySelectorAll<HTMLElement>('[data-word]'));

    // Delay one tick so layout is settled after hydration
    const id = setTimeout(() => {
      const ctx = gsap.context(() => {
        // Scroll-driven word fill animation
        if (allWords.length) {
          const total = allWords.length;

          const tl = gsap.timeline({
            scrollTrigger: {
              trigger: section,
              start: 'top 90%',
              end: 'bottom 60%',
              scrub: 1.5,
            },
          });

          allWords.forEach((word, i) => {
            const pos = i / total;
            const dur = 1 / total;

            tl.fromTo(
              word,
              { color: UNFILLED },
              { color: PRIMARY, duration: dur, ease: 'none' },
              pos,
            );

            if (word.dataset.bold !== undefined) {
              tl.fromTo(
                word,
                { fontWeight: 300 },
                { fontWeight: 500, duration: dur, ease: 'power1.out' },
                pos,
              );
            }
          });
        }

        // One-shot slide-in with bounce for the image
        if (imageRef.current) {
          gsap.fromTo(
            imageRef.current,
            { x: 80, opacity: 0 },
            {
              x: 0,
              opacity: 1,
              duration: 0.9,
              ease: 'back.out(1.4)',
              scrollTrigger: {
                trigger: imageRef.current,
                start: 'top 85%',
                toggleActions: 'play none none none',
              },
            },
          );
        }
      }, section);

      return () => ctx.revert();
    }, 100);

    return () => clearTimeout(id);
  }, []);

  if (!primary && !secondary) return null;

  const html = primary ? (asHTML(primary) ?? '') : '';
  const words = parseWords(html);
  // Pre-compute bold index for each word so JSX map stays pure
  let _boldCount = 0;
  const wordsWithBoldIndex = words.map(w => ({
    ...w,
    boldIndex: w.isBold ? _boldCount++ : -1,
  }));

  return (
    <section ref={sectionRef} className="bg-white py-14 sm:py-20">
      <div className="container mx-auto">
        <div className="mx-auto flex flex-col gap-10 lg:flex-row lg:items-center lg:gap-16">

          {/* Text — 60% */}
          <div className="relative lg:w-[66%]">
            {/* Image top-right on mobile only */}
            {image?.url && (
              <div ref={imageRef} className="absolute right-0 w-[100px] h-[100px] lg:hidden" style={{ top: '-45px' }}>
                <Image
                  src={image.url}
                  alt={image.alt ?? ''}
                  width={200}
                  height={200}
                  className="h-full w-full rounded-xl object-cover"
                />
              </div>
            )}
            <p className="mb-12 text-xs font-semibold uppercase tracking-widest text-yellow-400">
              The Problem
            </p>
            {wordsWithBoldIndex.length > 0 && (
              <p className="mb-4 font-light leading-tight text-3xl lg:text-4xl">
                {wordsWithBoldIndex.map((word, i) => (
                  <span
                    key={i}
                    data-word
                    {...(word.isBold ? { 'data-bold': '', 'data-bold-index': word.boldIndex } : {})}
                    style={{ color: UNFILLED, display: 'inline-block' }}
                  >
                    {word.text}
                    {i < wordsWithBoldIndex.length - 1 ? '\u00A0' : ''}
                  </span>
                ))}
              </p>
            )}
            {secondary && (
              <div className="text-base text-gray-500">
                <PrismicRichText field={secondary} />
              </div>
            )}
          </div>

          {/* Image — 40% desktop only */}
          {image?.url && (
            <div ref={imageRef} className="hidden lg:block lg:w-[34%]">
              <Image
                src={image.url}
                alt={image.alt ?? ''}
                width={1000}
                height={1000}
                className="h-auto w-full rounded-xl object-cover"
              />
            </div>
          )}

        </div>
      </div>
    </section>
  );
}
